import type { TFunction } from "i18next";
import type { HealthRecord, RecordKind } from "../types";
import type { WeightUnit } from "../preferences/types";
import { displayWeight, weightUnitLabel } from "./weightUnits";

export const recordKinds: RecordKind[] = ["weight", "vitals", "exercise", "appointment"];

export function localDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isLocalDateKey(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime()) && localDateKey(date) === value;
}

export function recordKindLabel(t: TFunction, kind: RecordKind, short = false): string {
  return t(short ? `recordKinds.short.${kind}` : `recordKinds.${kind}`);
}

export function recordValue(record: HealthRecord, t: TFunction, locale: string, weightUnit: WeightUnit = "kg"): string {
  switch (record.kind) {
    case "weight":
      return record.weightKilograms === undefined ? "—" : `${formatMeasurement(displayWeight(record.weightKilograms, weightUnit), locale)} ${weightUnitLabel(weightUnit)}`;
    case "vitals":
      if (record.systolic === undefined || record.diastolic === undefined) return "—";
      return record.pulseBpm === undefined
        ? `${record.systolic}/${record.diastolic} mmHg`
        : `${record.systolic}/${record.diastolic} mmHg · ${t("timeline.pulse", { value: record.pulseBpm })}`;
    case "exercise":
    case "appointment":
      return record.title ?? "—";
    default:
      return t("report.noNotes");
  }
}

export function compactRecordValue(record: HealthRecord, locale: string, weightUnit: WeightUnit = "kg"): string {
  switch (record.kind) {
    case "weight":
      return record.weightKilograms === undefined ? "—" : `${formatMeasurement(displayWeight(record.weightKilograms, weightUnit), locale)} ${weightUnitLabel(weightUnit)}`;
    case "vitals":
      return record.systolic === undefined || record.diastolic === undefined ? "—" : `${record.systolic}/${record.diastolic}`;
    case "exercise":
      return record.durationMinutes === undefined ? (record.title ?? "—") : `${record.durationMinutes} min`;
    case "appointment":
      return record.title ?? "—";
  }
}

export function formatMeasurement(value: number, locale: string, maximumFractionDigits = 1): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
}

export function recordDetail(record: HealthRecord, t: TFunction): string {
  switch (record.kind) {
    case "weight":
      return record.description ?? "";
    case "vitals":
      return record.description ?? "";
    case "exercise": {
      if (record.durationMinutes === undefined) return record.description ?? "";
      const duration = t("timeline.duration", { count: record.durationMinutes });
      return record.description ? `${duration} · ${record.description}` : duration;
    }
    case "appointment":
      return record.description ?? "";
  }
}

export function formatRecordTime(recordedAt: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(recordedAt));
}

export function formatRecordClock(recordedAt: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(new Date(recordedAt));
}

export function toLocalDateTimeInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
